import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import { GLOBAL } from 'saddlebag-browser';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import BpkCard from '@skyscanner/backpack-web/bpk-component-card';
import BpkImage, {
  withLazyLoading,
  withLoadingBehavior,
} from '@skyscanner/backpack-web/bpk-component-image';
import BpkSectionHeader from '@skyscanner/backpack-web/bpk-component-section-header';
import { TEXT_STYLES } from '@skyscanner/backpack-web/bpk-component-text';
import BpkText from '@skyscanner/backpack-web/bpk-component-text/src/BpkText';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import withErrorBoundary from '../ErrorBoundary/WithErrorBoundary';
import IntersectionObserverWrapper from '../IntersectionObserverWrapper/IntersectionObserverWrapper';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type { NoDealsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/NoDealsProps';

import STYLES from './NoDeals.module.scss';

const $window = GLOBAL.getWindow();

const IMG_WIDTH = 72;
const IMG_HEIGHT = 89;

const documentIfExists = typeof window !== 'undefined' ? document : null;
const LazyLoadedImage = withLoadingBehavior(
  withLazyLoading(BpkImage, documentIfExists),
);
export const CTA_ACTION = {
  LINK_TO_DAY_VIEW: 'LINK_TO_DAY_VIEW',
  BACK_TO_TOP: 'BACK_TO_TOP',
} as const;

const NoDeals = (props: NoDealsProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const handleClick = () => {
    switch (props.ctaAction) {
      case CTA_ACTION.BACK_TO_TOP:
        $window.scrollTo({ top: 0, behavior: 'smooth' });
        break;
    }
  };

  return (
    <div
      className={STYLES.NoDeals}
      data-testid="no-deals-component"
      data-tracking-element-id={`${rawComponentName}_no_deal`}
      data-tracking-common-params={buildMBDParam(`${rawComponentName}_no_deal`)}
    >
      <div className={STYLES.NoDeals__header}>
        <BpkSectionHeader title={props.title} description={props.subtitle} />
      </div>
      <BpkCard className={STYLES.NoDeals__content} atomic={false}>
        <div className={STYLES.NoDeals__section}>
          <BpkText
            tagName="h3"
            textStyle={
              props.isMobile ? TEXT_STYLES.heading5 : TEXT_STYLES.heading4
            }
            className={STYLES.NoDeals__subtitle}
          >
            {props.contentTitle}
          </BpkText>
          <BpkText
            tagName="p"
            textStyle={props.isMobile ? TEXT_STYLES.xs : TEXT_STYLES.base}
            className={STYLES.NoDeals__description}
          >
            {props.contentDescription}
          </BpkText>
          <div className={STYLES.NoDeals__button}>
            {props.isMobile ? (
              <BpkButtonV2
                href={props.dayViewLink}
                rel="nofollow"
                className={STYLES.NoDeals__mobileLink}
                onClick={handleClick}
                data-tracking-element-id={`${rawComponentName}_no_deal_cta`}
                data-tracking-common-params={buildMBDParam(
                  `${rawComponentName}_no_deal_cta`,
                )}
                type={BUTTON_TYPES.link}
              >
                <BpkText tagName="p" textStyle={TEXT_STYLES.label2}>
                  {props.buttonContent}
                </BpkText>
              </BpkButtonV2>
            ) : (
              <BpkButtonV2
                href={props.dayViewLink}
                rel="nofollow"
                onClick={handleClick}
                data-tracking-element-id={`${rawComponentName}_no_deal_cta`}
                data-tracking-common-params={buildMBDParam(
                  `${rawComponentName}_no_deal_cta`,
                )}
              >
                {props.buttonContent}
              </BpkButtonV2>
            )}
          </div>
        </div>
        <div className={STYLES.NoDeals__image}>
          {props.imageUrl && (
            <div className={STYLES.NoDeals__logo}>
              <LazyLoadedImage
                alt={props.buttonContent}
                src={props.imageUrl}
                aspectRatio={IMG_WIDTH / IMG_HEIGHT}
              />
            </div>
          )}
        </div>
      </BpkCard>
    </div>
  );
};

const NoDealsWrapper = (props: NoDealsProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.NO_DEALS,
      })
    }
  >
    <NoDeals {...props} />
  </IntersectionObserverWrapper>
);

const NoDealsWithErrorBoundary = withErrorBoundary(
  NoDealsWrapper,
  'no-deals-component',
);
export default NoDealsWithErrorBoundary;
